import { StationSummary } from "./dtos/StationSummary.schema.d";
import { Operator } from "./dtos/Operator.schema.d";
import { OperatorSummary } from "./dtos/OperatorSummary.schema";
import axios from "axios";
import { OperatorContactData } from "./dtos/OperatorContactData.schema";
import { OperatorFinanceData } from "./dtos/OperatorFinanceData.schema";
import { DocumentsHistory } from "./dtos/DocumentsHistory.schema";
import { OperatorDocumentSettings } from "./dtos/OperatorDocumentSettings.schema";
import { Card } from "./dtos/Card.schema";
import { AxiosResponse } from "axios";
import { ReminderMailSettings } from "./dtos/ReminderMailSettings.schema";

export const getOperators = async (): Promise<OperatorSummary[]> => {
  const res = await axios.get<OperatorSummary[]>("/operators");
  return res.data;
};

export const createOperator = async (operator: Operator): Promise<Operator> => {
  const res = await axios.post<Operator>("/operators", operator);
  return res.data;
};

export const getOperator = async (operatorId: string): Promise<Operator> => {
  const res = await axios.get<Operator>(`/operators/${operatorId}`);
  return res.data;
};

export const deleteOperator = async (operatorId: string): Promise<void> => {
  const res = await axios.delete<void>(`/operators/${operatorId}`);
};

export const updateIsOperatorActive = async (
  operatorId: string,
  isOperatorActive: boolean
): Promise<void> => {
  const res = await axios.put<void>(
    `/operators/${operatorId}/active`,
    isOperatorActive,
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
};

/**
 * caches the operator's logo, returns the image url
 */
export const preloadOperatorLogo = async (
  operatorId: string
): Promise<File | undefined> => {
  const res = await axios.get(`/operators/${operatorId}/logo`, {
    responseType: "blob",
  });
  if (res && res.data) return res.data;
  return null as unknown as File;
};

export const updateOperatorLogoPng = async (
  operatorId: string,
  imageFile: File
): Promise<void> => {
  const res = await axios.put<void>(
    `/operators/${operatorId}/logo`,
    imageFile,
    {
      headers: {
        "Content-Type": imageFile.type,
      },
    }
  );
};

export const addManager = async (
  operatorId: string,
  email: string
): Promise<void> => {
  const res = await axios.put<void>(
    `/operators/${operatorId}/managers`,
    {
      email: email,
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  console.log("manager status code", res.status);
};

export const deleteManager = async (
  operatorId: string,
  userId: string
): Promise<void> => {
  const res = await axios.delete<void>(
    `/operators/${operatorId}/managers/${userId}`
  );
  console.log(res.status);
};

export const setManagerStations = async (
  operatorId: string,
  userId: string,
  stationIds: string[]
): Promise<void> => {
  const res = await axios.put<void>(
    `/operators/${operatorId}/managers/${userId}/stations`,
    stationIds
  );
};

export const addOwner = async (
  operatorId: string,
  email: string
): Promise<void> => {
  const res = await axios.put<void>(
    `/operators/${operatorId}/owners`,
    {
      email: email,
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  console.log(res.status);
};

export const resendOwnerInvite = async (
  operatorId: string,
  email: string
): Promise<void> => {
  await axios.post<void>(`/operators/${operatorId}/owners/resend`, { email }, {
    headers: { "Content-Type": "application/json" },
  });
};

export const resendManagerInvite = async (
  operatorId: string,
  email: string
): Promise<void> => {
  await axios.post<void>(`/operators/${operatorId}/managers/resend`, { email }, {
    headers: { "Content-Type": "application/json" },
  });
};

export const deleteOwner = async (
  operatorId: string,
  userId: string
): Promise<void> => {
  const res = await axios.delete<void>(
    `/operators/${operatorId}/owners/${userId}`
  );

  console.log(res.status);
};

export const updateOperatorContactData = async (
  operatorId: string,
  contact: OperatorContactData
): Promise<void> => {
  const res = await axios.put<void>(
    `/operators/${operatorId}/contact`,
    contact
  );
};

export const updateOperatorFinanceData = async (
  operatorId: string,
  financeData: OperatorFinanceData
): Promise<AxiosResponse<void>> => {
  return await axios.put<void>(
    `/operators/${operatorId}/finance`,
    financeData,
    {
      validateStatus: () => true,
    }
  );
};

export const getOperatorDocumentsHistory = async (
  operatorId: string
): Promise<DocumentsHistory> => {
  const res = await axios.get<DocumentsHistory>(
    `/operators/${operatorId}/documents/history`
  );
  return res.data;
};

export const downloadLatestOperatorGdrp = async (
  operatorId: string
): Promise<string> => {
  const res = await axios.get<any>(`/operators/${operatorId}/documents/gdpr`);
  return res.data;
};

export const downloadOperatorGdrp = async (
  operatorId: string,
  version: string
): Promise<string> => {
  const res = await axios.get<string>(
    `/operators/${operatorId}/documents/gdpr/${version}`
  );
  return res.data;
};

export const uploadOperatorGdrp = async (
  operatorId: string,
  text: string
): Promise<void> => {
  const res = await axios.post<void>(
    `/operators/${operatorId}/documents/gdprw`,
    {text: text}
  );
};

export const downloadLatestOperatorGdrpw = async (
  operatorId: string
): Promise<string> => {
  const res = await axios.get<any>(`/operators/${operatorId}/documents/gdprw`);
  return res.data;
};

export const downloadOperatorGdprw = async (
  operatorId: string,
  version: string
): Promise<string> => {
  const res = await axios.get<string>(
    `/operators/${operatorId}/documents/gdprw/${version}`
  );
  return res.data;
};

export const uploadOperatorGdprw = async (
  operatorId: string,
  text: string
): Promise<void> => {
  const res = await axios.post<void>(
    `/operators/${operatorId}/documents/gdprw`,
    {text: text}
  );
};

export const downloadLatestOperatorGtc = async (
  operatorId: string
): Promise<string> => {
  const res = await axios.get<any>(`/operators/${operatorId}/documents/gtc`);
  return res.data;
};

export const downloadOperatorGtc = async (
  operatorId: string,
  version: string
): Promise<string> => {
  const res = await axios.get<string>(
    `/operators/${operatorId}/documents/gtc/${version}`
  );
  return res.data;
};

export const uploadOperatorGtc = async (
  operatorId: string,
  text: string
): Promise<void> => {
  const res = await axios.post<void>(
    `/operators/${operatorId}/documents/gtc`,
    {text: text}
  );
};

export const updateOperatorDocumentSettings = async (
  operatorId: string,
  documentSettings: OperatorDocumentSettings
): Promise<void> => {
  const res = await axios.put<void>(
    `/operators/${operatorId}/documents/settings`,
    documentSettings
  );
};

export const createCard = async (
  operatorId: string,
  card: Card
): Promise<AxiosResponse> => {
  return await axios.post<void>(
    `/operators/${operatorId}/cards`, 
    card, 
    {
      validateStatus: () => true // Accept any status code without throwing
    }
  );
};

export const updateCard = async (
  operatorId: string,
  card: Card
): Promise<void> => {
  const res = await axios.put<void>(
    `/operators/${operatorId}/cards/${card.uid}`,
    card
  );
};

export const deleteCard = async (
  operatorId: string,
  card: Card
): Promise<void> => {
  const res = await axios.delete<void>(
    `/operators/${operatorId}/cards/${card.uid}`
  );
};

export const updateMailTemplate = async (
  operatorId: string,
  templatedId: string,
  rtfContent: string
): Promise<void> => {
  const res = await axios.put<void>(
    `/operators/${operatorId}/mail-templates/${templatedId}`,
    { content: rtfContent },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
};

export const updatePlatformServiceActive = async (
  operatorId: string,
  platformServiceActive: boolean
): Promise<void> => {
  const res = await axios.put<void>(
    `/operators/${operatorId}/platform-service`,
    platformServiceActive,
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
};

export const updateReminderMailSettings = async (
  operatorId: string,
  reminderMailSettings: ReminderMailSettings
): Promise<void> => {
  const res = await axios.put<void>(
    `/operators/${operatorId}/misc/reminder-settings`,
    reminderMailSettings
  );
  console.log(res.status);
};
